import { supabase } from '../config/supabase';
import { logger } from '../utils/logger';
import i18n from '../i18n/config.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const ANALYTICS_SESSION_KEY = 'revetall-analytics-session-id';
const ANALYTICS_DEDUPE_WINDOW_MS = 10_000;
const ANALYTICS_DEDUPE_STORE_KEY = 'revetall-analytics-dedupe-v1';
const _analyticsLastSentAt = new Map();

const _readDedupeStore = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(ANALYTICS_DEDUPE_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const _writeDedupeStore = (store) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(ANALYTICS_DEDUPE_STORE_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
};

const getAnalyticsSessionId = () => {
  if (typeof window === 'undefined') return null;
  try {
    const existing = sessionStorage.getItem(ANALYTICS_SESSION_KEY);
    if (existing) return existing;
    const generated = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).toString();
    sessionStorage.setItem(ANALYTICS_SESSION_KEY, generated);
    return generated;
  } catch {
    return null;
  }
};

export const trackUserEvent = async ({ companyId, event, operation = 'read', path = null }) => {
  try {
    const sessionId = getAnalyticsSessionId();
    if (!sessionId) return;

    // Dedupe/cooldown guard (helps with React StrictMode double effects)
    const dedupeKey = `${sessionId}|${companyId}|${event}|${operation}|${path ?? ''}`;
    const now = Date.now();
    const inMemLast = _analyticsLastSentAt.get(dedupeKey);
    if (inMemLast && now - inMemLast < ANALYTICS_DEDUPE_WINDOW_MS) return;

    const store = _readDedupeStore();
    const persistedLast = store[dedupeKey];
    if (persistedLast && now - persistedLast < ANALYTICS_DEDUPE_WINDOW_MS) return;

    _analyticsLastSentAt.set(dedupeKey, now);
    store[dedupeKey] = now;
    _writeDedupeStore(store);

    const token = await getAuthToken();
    if (!token || !companyId) return;

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    // Fire-and-forget: do not await response body
    fetch(`${API_BASE}/analytics/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session_id: sessionId,
        event,
        operation,
        path
      })
    }).catch(() => {});
  } catch {
    // Swallow analytics failures silently
  }
};

/**
 * Get current user's JWT token for API authentication
 * @returns {Promise<string|null>} JWT token or null if not authenticated
 */
const getAuthToken = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      logger.error('Error getting session:', error);
      return null;
    }
    
    if (!session) {
      logger.warn('No active session found');
      return null;
    }
    
    // Check if the session is expired
    if (session.expires_at && new Date(session.expires_at * 1000) <= new Date()) {
      logger.warn('Session expired, attempting to refresh...');
      
      // Try to refresh the session
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !refreshData.session) {
        logger.error('Failed to refresh session:', refreshError);
        return null;
      }
      
      return refreshData.session.access_token;
    }
    
    return session.access_token;
  } catch (error) {
    logger.error('Error in getAuthToken:', error);
    return null;
  }
};

/**
 * Send a question to the Agent API
 * @param {string} query - The user's question
 * @param {string} [sessionId] - Optional session ID. Omit to let the API create a thread.
 * @param {number} companyId - ID of the company to query data for
 * @param {Object} [inputParameters] - Fields for this turn (project_id, dates). Omit keys that are unset.
 * @returns {Promise<Object>} API response with explanation and chart data
 */
export const askQuestion = async (query, sessionId, companyId, inputParameters) => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Validate company ID
    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    // Prepare headers with authentication (Session-Id for conversation continuity)
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId
    };
    if (sessionId) {
      headers['Session-Id'] = sessionId;
    }

    const body = { query };
    if (inputParameters && Object.keys(inputParameters).length) {
      body.input_parameters = inputParameters;
    }

    const response = await fetch(`${API_BASE}/ask`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        // Try to get the specific error message from the response
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('API call failed:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      // Could trigger a logout or redirect to login here
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};

/**
 * Run a previously stored query by query_id
 * @param {number} queryId - The ID of the stored query to run
 * @param {number} companyId - ID of the company to query data for
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request (e.g. on company switch)
 * @returns {Promise<Object>} API response with query results
 */
export const runQuery = async (queryId, companyId, signal = null) => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Validate inputs
    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }
    if (!queryId) {
      throw new Error('Query ID is required');
    }

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId
    };

    const fetchOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify({ query_id: queryId })
    };
    if (signal) fetchOptions.signal = signal;

    const response = await fetch(`${API_BASE}/run_query`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        // User doesn't have permission to run this query (different company)
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noQueryAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noQueryAccess'));
        }
      }
      if (response.status === 404) {
        throw new Error(i18n.t('errors.queryNotFound'));
      }
      if (response.status === 400) {
        // Failed query or execution error
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.queryExecutionFailed');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.queryExecutionFailed'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('Run query API call failed:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};

/**
 * Load a backend conversation by session_id. Does not send Company-Id so a
 * deep link can resolve the session's own company.
 * @param {string} sessionId
 * @returns {Promise<{session_id: string, company_id: number, title: string|null, messages: Array}>}
 */
export const getConversation = async (sessionId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error(i18n.t('errors.noAuthToken'));
  }
  if (!sessionId) {
    const error = new Error('session_id required');
    error.status = 400;
    throw error;
  }

  const response = await fetch(
    `${API_BASE}/conversations?session_id=${encodeURIComponent(sessionId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      signal: AbortSignal.timeout(15000)
    }
  );

  if (response.status === 404) {
    const error = new Error('Conversation not found');
    error.status = 404;
    throw error;
  }
  if (response.status === 401) {
    throw new Error(i18n.t('errors.authFailed'));
  }
  if (!response.ok) {
    throw new Error(i18n.t('errors.httpError', { status: response.status }));
  }
  return response.json();
};

/**
 * Load share picker members and grants. Owner only. Does not send Company-Id.
 * @param {string} sessionId
 * @returns {Promise<{session_id: string, company_id: number, members: Array, granted_user_ids: string[]}>}
 */
export const getConversationGrants = async (sessionId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error(i18n.t('errors.noAuthToken'));
  }
  const response = await fetch(
    `${API_BASE}/conversations/grants?session_id=${encodeURIComponent(sessionId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      signal: AbortSignal.timeout(15000)
    }
  );
  if (response.status === 404) {
    const error = new Error(i18n.t('chat.shareNotFound'));
    error.status = 404;
    throw error;
  }
  if (response.status === 401) {
    throw new Error(i18n.t('errors.authFailed'));
  }
  if (!response.ok) {
    throw new Error(i18n.t('errors.httpError', { status: response.status }));
  }
  return response.json();
};

/**
 * Replace grant rows for a session. Owner only. Does not send Company-Id.
 * @param {string} sessionId
 * @param {string[]} userIds
 * @returns {Promise<{session_id: string, company_id: number, members: Array, granted_user_ids: string[]}>}
 */
export const putConversationGrants = async (sessionId, userIds) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error(i18n.t('errors.noAuthToken'));
  }
  const response = await fetch(
    `${API_BASE}/conversations/grants?session_id=${encodeURIComponent(sessionId)}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user_ids: userIds }),
      signal: AbortSignal.timeout(15000)
    }
  );
  if (response.status === 404) {
    const error = new Error(i18n.t('chat.shareNotFound'));
    error.status = 404;
    throw error;
  }
  if (response.status === 401) {
    throw new Error(i18n.t('errors.authFailed'));
  }
  if (!response.ok) {
    throw new Error(i18n.t('errors.httpError', { status: response.status }));
  }
  return response.json();
};

/**
 * Email granted users a conversation link. Owner only. Does not send Company-Id.
 * @param {string} sessionId
 * @param {{message: string, conversation_url: string, language: 'en'|'no'}} body
 * @returns {Promise<{results: Array<{recipient: string, ok: boolean, resend_id?: string, error?: string}>}>}
 */
export const postConversationEmails = async (sessionId, body) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error(i18n.t('errors.noAuthToken'));
  }
  const response = await fetch(
    `${API_BASE}/conversations/emails?session_id=${encodeURIComponent(sessionId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(30000)
    }
  );
  if (response.status === 404) {
    const error = new Error(i18n.t('chat.shareNotFound'));
    error.status = 404;
    throw error;
  }
  if (response.status === 401) {
    throw new Error(i18n.t('errors.authFailed'));
  }
  if (!response.ok) {
    throw new Error(i18n.t('errors.httpError', { status: response.status }));
  }
  return response.json();
};

/**
 * Get companies that the current user has access to
 * @returns {Promise<Object>} API response with companies list
 */
export const getUserCompanies = async () => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    const response = await fetch(`${API_BASE}/user/companies`, {
      method: 'GET',
      headers,
      signal: AbortSignal.timeout(10000) // 10 second timeout
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        // Try to get the specific error message from the response
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyAccess'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('Failed to fetch user companies:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};

/**
 * Store company secrets (e.g., Tripletex tokens)
 * @param {string} integrationType - Type of integration (e.g., 'tripletex')
 * @param {Array} secrets - Array of {name, value} objects
 * @param {number} companyId - ID of the company to store secrets for
 * @returns {Promise<Object>} API response with stored secrets info
 */
export const storeCompanySecrets = async (integrationType, secrets, companyId) => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Validate inputs
    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequiredForSecrets'));
    }
    if (!integrationType) {
      throw new Error(i18n.t('errors.integrationTypeRequired'));
    }
    if (!secrets || !Array.isArray(secrets) || secrets.length === 0) {
      throw new Error(i18n.t('errors.secretsArrayRequired'));
    }

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const response = await fetch(`${API_BASE}/company/secrets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        integration_type: integrationType,
        secrets: secrets
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        // Try to get the specific error message from the response
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noSecretsPermission');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noSecretsPermission'));
        }
      }
      if (response.status === 400) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.invalidRequestData');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.invalidRequestData'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('Failed to store company secrets:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};

/**
 * Get metadata about all stored company secrets (without actual secret values)
 * Returns secrets from all integration types
 * @param {number} companyId - ID of the company to get secrets for
 * @returns {Promise<Object>} API response with secrets metadata
 */
export const getCompanySecretsMetadata = async (companyId) => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Validate inputs
    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequiredForMetadata'));
    }

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const response = await fetch(`${API_BASE}/company/secrets`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        // Try to get the specific error message from the response
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noSecretsViewPermission');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noSecretsViewPermission'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('Failed to get company secrets metadata:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};

/**
 * Get all projects for a company
 * @param {number} companyId - ID of the company to get projects for
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Object>} API response with projects grouped by open/closed
 */
export const getProjects = async (companyId, signal = null) => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Validate company ID
    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const fetchOptions = { method: 'GET', headers };
    if (signal) fetchOptions.signal = signal;
    else fetchOptions.signal = AbortSignal.timeout(10000);

    const response = await fetch(`${API_BASE}/projects`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('Failed to fetch projects:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};

/**
 * Get details for one reporting-family project.
 * @param {number} companyId - ID of the company to get the project for
 * @param {number|string} projectId - Reporting-family root id
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @param {number[]|null} [projectIds] - Booking projects to include. Null or empty is the family.
 * @returns {Promise<Object|null>} Project details, or null when not found
 */
export const getProject = async (companyId, projectId, signal = null, projectIds = null) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const fetchOptions = { method: 'GET', headers };
    if (signal) fetchOptions.signal = signal;
    else fetchOptions.signal = AbortSignal.timeout(10000);

    const params = new URLSearchParams();
    (projectIds || []).forEach((id) => params.append('project_ids', String(id)));
    const query = params.toString();
    const response = await fetch(
      `${API_BASE}/projects/${projectId}${query ? `?${query}` : ''}`,
      fetchOptions
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          if (parseError.message && parseError.message !== i18n.t('errors.noCompanyDataAccess')) {
            throw parseError;
          }
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    return await response.json();
  } catch (error) {
    logger.error('Failed to fetch project details:', error);
    throw error;
  }
};

/**
 * Get warnings for one reporting-family project.
 * @param {number} companyId - ID of the company to get warnings for
 * @param {number|string} projectId - Reporting-family root id
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Object>} API response with overdue_invoices
 */
export const getProjectWarnings = async (companyId, projectId, signal = null) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const fetchOptions = { method: 'GET', headers };
    if (signal) fetchOptions.signal = signal;
    else fetchOptions.signal = AbortSignal.timeout(10000);

    const response = await fetch(`${API_BASE}/projects/${projectId}/warnings`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    return await response.json();
  } catch (error) {
    logger.error('Failed to fetch project warnings:', error);
    throw error;
  }
};

/**
 * Get future cashflow data for a company
 * @param {number} companyId - ID of the company to query data for
 * @param {string} fromDate - Optional start date in YYYY-MM-DD format (defaults to today)
 * @param {string} toDate - Optional end date in YYYY-MM-DD format
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Object>} API response with cashflow data
 */
export const getCashflow = async (companyId, fromDate = null, toDate = null, signal = null) => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Validate company ID
    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    // Build query string with optional date parameters
    const queryParams = new URLSearchParams();
    if (fromDate) {
      queryParams.append('from_date', fromDate);
    }
    if (toDate) {
      queryParams.append('to_date', toDate);
    }
    const queryString = queryParams.toString();
    const url = `${API_BASE}/cashflow${queryString ? `?${queryString}` : ''}`;

    const fetchOptions = {
      method: 'GET',
      headers,
      signal: signal || AbortSignal.timeout(10000)
    };

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      if (response.status === 400) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.invalidDateRange');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.invalidDateRange'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('Failed to fetch cashflow data:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};

/**
 * Get cashflow scenario with optional event types included.
 * @param {number} companyId - ID of the company to query data for
 * @param {string[]} eventTypes - Optional event types (e.g. projected_outbound_invoice)
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Object>} Scenario days in data, plus cash-only baseline points
 */
export const getCashflowScenario = async (companyId, eventTypes = [], signal = null) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const fetchOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_types: eventTypes }),
      signal: signal || AbortSignal.timeout(10000)
    };

    const response = await fetch(`${API_BASE}/cashflow/scenario`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          if (parseError.message && parseError.message !== i18n.t('errors.noCompanyDataAccess')) {
            throw parseError;
          }
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      if (response.status === 400) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.invalidDateRange');
          throw new Error(detail);
        } catch (parseError) {
          if (parseError.message && parseError.message !== i18n.t('errors.invalidDateRange')) {
            throw parseError;
          }
          throw new Error(i18n.t('errors.invalidDateRange'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    return await response.json();
  } catch (error) {
    logger.error('Failed to fetch cashflow scenario:', error);

    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }

    throw error;
  }
};

/**
 * Get contract backlog timeseries for a company
 * @param {string|number} companyId - Company ID
 * @param {string|null} fromDate - Start date YYYY-MM-DD (optional)
 * @param {string|null} toDate - End date YYYY-MM-DD (optional)
 * @param {AbortSignal|null} signal - Optional abort signal
 * @returns {Promise<{data: Array}>}
 */
export const getContractBacklog = async (companyId, fromDate = null, toDate = null, signal = null) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const queryParams = new URLSearchParams();
    if (fromDate) {
      queryParams.append('from_date', fromDate);
    }
    if (toDate) {
      queryParams.append('to_date', toDate);
    }
    const queryString = queryParams.toString();
    const url = `${API_BASE}/contract-backlog${queryString ? `?${queryString}` : ''}`;

    const fetchOptions = {
      method: 'GET',
      headers,
      signal: signal || AbortSignal.timeout(10000)
    };

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      if (response.status === 400) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.invalidDateRange');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.invalidDateRange'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    return await response.json();
  } catch (error) {
    logger.error('Failed to fetch contract backlog data:', error);

    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }

    throw error;
  }
};

/**
 * Get customer receivables for a company.
 * @param {string|number} companyId - Company ID
 * @param {number} maxAgeDays - Past-due window: 91, 182, 365, or 730
 * @param {AbortSignal|null} signal - Optional abort signal
 * @returns {Promise<{customers: Array}>}
 */
export const getReceivables = async (companyId, maxAgeDays, signal = null) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const queryParams = new URLSearchParams();
    queryParams.append('max_age_days', String(maxAgeDays));
    const fetchOptions = {
      method: 'GET',
      headers,
      signal: signal || AbortSignal.timeout(10000)
    };

    const response = await fetch(`${API_BASE}/receivables?${queryParams.toString()}`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    return await response.json();
  } catch (error) {
    logger.error('Failed to fetch receivables:', error);

    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }

    throw error;
  }
};

/**
 * Get KPIs for a company
 * @param {number} companyId - ID of the company to query data for
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Object>} API response with KPI data
 */
export const getKPIs = async (companyId, signal = null) => {
  try {
    // Get JWT token for authentication
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    // Validate company ID
    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const fetchOptions = {
      method: 'GET',
      headers,
      signal: signal || AbortSignal.timeout(10000)
    };

    const response = await fetch(`${API_BASE}/kpis`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          const detail = errorData.detail || i18n.t('errors.noCompanyDataAccess');
          throw new Error(detail);
        } catch (parseError) {
          throw new Error(i18n.t('errors.noCompanyDataAccess'));
        }
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    logger.error('Failed to fetch KPIs:', error);
    
    // Handle authentication errors specifically
    if (error.message.includes('No authentication token') || error.message.includes('Authentication failed')) {
      logger.error('Authentication error - user may need to log in again');
    }
    
    throw error;
  }
};
